import { getSupabaseClient } from "@veorest/api-client";
import { create } from "zustand";

export type PartnerRole = "owner" | "ops" | "viewer" | string;

export interface PartnerSession {
  partnerId: string;
  userId: string;
  displayName: string;
  role: PartnerRole;
  partnerName?: string;
}

interface PartnerSessionState {
  session: PartnerSession | null;
  resolving: boolean;
  setSession: (session: PartnerSession | null) => void;
  setResolving: (resolving: boolean) => void;
  clear: () => void;
}

export const usePartnerSessionStore = create<PartnerSessionState>((set) => ({
  session: null,
  resolving: false,
  setSession: (session) => set({ session }),
  setResolving: (resolving) => set({ resolving }),
  clear: () => set({ session: null }),
}));

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** Resolve partner membership for the current Supabase auth user. Fail closed. */
export async function resolvePartnerSession(): Promise<PartnerSession | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_get_auth_session");

  if (error || data == null) return null;

  const row = asRecord(data);
  if (!row) return null;

  const partnerId = String(row.partner_id ?? "").trim();
  if (!partnerId) return null;

  const base: PartnerSession = {
    partnerId,
    userId: String(row.user_id ?? ""),
    displayName: String(row.display_name ?? ""),
    role: String(row.role ?? "viewer"),
  };

  try {
    const { data: profile } = await supabase.rpc("partner_get_profile", {
      p_partner_id: partnerId,
    });
    const profileRow = asRecord(profile);
    if (profileRow?.name) {
      base.partnerName = String(profileRow.name);
    }
  } catch {
    // Profile is optional for Step 1 shell; session gate already passed.
  }

  return base;
}

/** Username → internal login email; real emails pass through. */
export function toLoginEmail(raw: string): string {
  const trimmed = raw.trim().toLowerCase();
  const bare = trimmed.startsWith("@") ? trimmed.slice(1) : trimmed;
  if (!bare) return trimmed;
  return bare.includes("@") ? bare : `${bare}@users.oelox.internal`;
}

/** Supabase auth error → friendly i18n key (auth page toasts). */
export function authErrorKey(rawMessage: string): string {
  const msg = (rawMessage || "").toLowerCase();
  if (
    msg.includes("invalid login") ||
    msg.includes("invalid_credentials") ||
    msg.includes("user not found") ||
    msg.includes("invalid grant")
  ) {
    return "auth.invalidCredentials";
  }
  if (msg.includes("email not confirmed") || msg.includes("not confirmed")) {
    return "auth.emailNotConfirmed";
  }
  if (msg.includes("too many") || msg.includes("rate limit")) {
    return "auth.tooMany";
  }
  if (
    msg.includes("failed to fetch") ||
    msg.includes("network") ||
    msg.includes("timeout")
  ) {
    return "auth.networkError";
  }
  return "auth.signInFailed";
}

export function mustChangePassword(user: {
  user_metadata?: Record<string, unknown>;
} | null): boolean {
  return user?.user_metadata?.must_change_password === true;
}

export async function completeMandatoryPasswordChange(
  newPassword: string
): Promise<void> {
  if (newPassword.length < 8) {
    throw new Error("password_too_short");
  }
  const supabase = getSupabaseClient();
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
    data: { must_change_password: false },
  });
  if (error) throw error;
}
